import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Package, Warehouse, ArrowLeftRight, AlertTriangle, Wrench,
  Factory, TrendingDown, Plus, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function InventoryDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      const res = await inventoryAPI.dashboard();
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally { setLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading dashboard...</div>;
  if (!data) return <div className="text-center text-sm text-muted-foreground py-12">Failed to load data</div>;

  const statCards = [
    { label: 'Total Items', value: data.total_items, icon: Package, color: 'text-orange-600', bg: 'bg-orange-50', link: '/inventory/items' },
    { label: 'Fixed Assets', value: data.fixed_assets, icon: Wrench, color: 'text-blue-600', bg: 'bg-blue-50', link: '/inventory/items?type=Fixed+Asset' },
    { label: 'Raw Materials', value: data.raw_materials, icon: Package, color: 'text-emerald-600', bg: 'bg-emerald-50', link: '/inventory/items?type=Raw+Material' },
    { label: 'Consumables', value: data.consumables, icon: Package, color: 'text-violet-600', bg: 'bg-violet-50', link: '/inventory/items?type=Consumable' },
    { label: 'Godowns', value: data.total_godowns, icon: Warehouse, color: 'text-cyan-600', bg: 'bg-cyan-50', link: '/inventory/godowns' },
    { label: 'Transactions', value: data.total_transactions, icon: ArrowLeftRight, color: 'text-amber-600', bg: 'bg-amber-50', link: '/inventory/transactions' },
    { label: 'Semi-Finished', value: data.semi_finished, icon: Package, color: 'text-slate-600', bg: 'bg-slate-50', link: '/inventory/items?type=Semi-Finished+Part' },
    { label: 'Finished Goods', value: data.finished_goods || 0, icon: Package, color: 'text-teal-600', bg: 'bg-teal-50', link: '/inventory/items?type=Finished+Good' },
  ];

  return (
    <div className="space-y-6" data-testid="inventory-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Inventory</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Overview of items, stock, and godowns</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => navigate('/inventory/items/new')} data-testid="new-item-btn">
            <Plus className="w-4 h-4 mr-1" /> New Item
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/inventory/transactions/new')} data-testid="new-transaction-btn">
            <ArrowLeftRight className="w-4 h-4 mr-1" /> Receive / Issue
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {statCards.map(card => (
          <Card key={card.label} className="cursor-pointer hover:shadow-sm transition-shadow border" onClick={() => navigate(card.link)} data-testid={`stat-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className={`p-1.5 rounded-md ${card.bg}`}>
                  <card.icon className={`w-4 h-4 ${card.color}`} />
                </div>
              </div>
              <p className="font-heading text-2xl font-bold">{card.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{card.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Low Stock Items */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Low Stock Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.low_stock_items.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No low stock alerts</p>
            ) : (
              <div className="space-y-2">
                {data.low_stock_items.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-2 rounded-md bg-amber-50 border border-amber-100 cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => navigate(`/inventory/items/${item.id}`)}>
                    <div>
                      <p className="text-xs font-medium">{item.item_name}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">{item.unique_id}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">
                        <TrendingDown className="w-3 h-3 mr-1" />
                        {item.total_stock} / {item.min_stock_level}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ArrowLeftRight className="w-4 h-4 text-blue-500" />
              Recent Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.recent_transactions.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No transactions yet</p>
            ) : (
              <div className="space-y-2">
                {data.recent_transactions.map(txn => (
                  <div key={txn.id} className="flex items-center justify-between p-2 rounded-md bg-muted/50 border">
                    <div>
                      <p className="text-xs font-medium">{txn.item_name || 'Item'}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {txn.transaction_type} - {txn.quantity} {txn.godown_name && `at ${txn.godown_name}`}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                      {txn.transaction_type}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
